import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { dsCrossOrgSnapshotService } from '@services/dsCrossOrgSnapshotService';
import { dsFileService } from '@services/dsFileService';
import { PdfViewer } from '@workpay/ds-shared/components/PdfViewer';
import { OnlyOfficeEditor } from '@workpay/ds-shared/components/OnlyOfficeEditor';
import { ChatWidget } from '@workpay/ds-shared/components/ChatWidget';
import { useReviewSse } from '@workpay/ds-shared/hooks/useReviewSse';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import { useTranslation } from '@hooks/useTranslation';
import { useCountries } from '@hooks/useCountries';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@stores/authStore';
import { useBreadcrumbOverride } from '@stores/breadcrumbStore';
import { getTenants } from '@services/tenantService';
import { getLegalEntities } from '@services/legalEntityService';
import { getEmploymentTypes } from '@services/employmentTypeService';
import { getDocumentTypes } from '@services/documentTypeService';
import { toTitleCase } from '@utils/textCase';
import { STATUS_CLASS_MAP, FORMAT_CLASS_MAP } from './snapshotAdminBadges';
import { SnapshotReviewPanel } from './SnapshotReviewPanel';
import { DeactivateModal } from './DeactivateModal';
import styles from './SnapshotAdminDetail.module.scss';

interface SnapshotAdminDetailProps {
  snapshotId: string;
}

export function SnapshotAdminDetail({ snapshotId }: SnapshotAdminDetailProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const setOverride = useBreadcrumbOverride((s) => s.setOverride);

  const [splitPercent, setSplitPercent] = useState(50);
  const [isExpanded, setIsExpanded] = useState(false);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  // Escape key exits fullscreen
  useEffect(() => {
    if (!isExpanded) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsExpanded(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isExpanded]);

  // rAF-throttled split drag
  const handleSplitStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const container = splitContainerRef.current;
    if (!container) return;
    let rafId = 0;
    const onMouseMove = (ev: MouseEvent) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const rect = container.getBoundingClientRect();
        const pct = ((ev.clientX - rect.left) / rect.width) * 100;
        setSplitPercent(Math.max(25, Math.min(75, pct)));
      });
    };
    const onMouseUp = () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // ── Data fetching ──
  const { data: snapshot, isLoading, error: snapshotErrorObj } = useQuery({
    queryKey: ['dsCrossOrgSnapshot', snapshotId],
    queryFn: () => dsCrossOrgSnapshotService.getById(snapshotId),
  });
  const snapshotForbidden = isForbiddenError(snapshotErrorObj);

  // Breadcrumb
  useEffect(() => {
    if (snapshot?.name) {
      setOverride(`/w/contracts/${snapshotId}`, snapshot.name);
      return () => setOverride(`/w/contracts/${snapshotId}`, null);
    }
  }, [snapshot?.name, snapshotId, setOverride]);

  // Classification name resolution
  const { countries } = useCountries();
  const countryName = countries.find((c) => c.id === snapshot?.countryId)?.name;

  const { data: legalEntitiesData } = useQuery({
    queryKey: ['legalEntities', { countryId: snapshot?.countryId, size: 100 }],
    queryFn: () => getLegalEntities({ size: 100 }, { filters: { countryId: snapshot!.countryId! } }),
    enabled: !!snapshot?.countryId,
  });
  const legalEntityName = legalEntitiesData?.content?.find((le) => le.id === snapshot?.legalEntityId)?.name;

  const { data: employmentTypesData } = useQuery({
    queryKey: ['employmentTypes', { size: 100 }],
    queryFn: () => getEmploymentTypes({ size: 100 }),
  });
  const employmentTypeName = employmentTypesData?.content?.find((et) => et.id === snapshot?.employmentTypeId)?.name;

  const { data: documentTypesData } = useQuery({
    queryKey: ['documentTypesAll', { size: 100 }],
    queryFn: () => getDocumentTypes({ size: 100 }),
  });
  const documentTypeName = documentTypesData?.content?.find((dt) => dt.id === snapshot?.documentTypeId)?.name;

  // Tenant name
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants', 'all', { size: 300 }],
    queryFn: () => getTenants({ size: 300 }),
  });
  const tenantName = useMemo(() => {
    if (!snapshot?.organizationId || !tenantsData?.content) return undefined;
    // Match against `organization.id` (auth-side org id), not `tenant.organizationId`
    // which does not exist on the response.
    return tenantsData.content.find((tw) => tw.organization?.id === snapshot.organizationId)?.tenant.name;
  }, [snapshot?.organizationId, tenantsData]);

  // Signing fields
  const { data: signingFields = [] } = useQuery({
    queryKey: ['dsCrossOrgSnapshot-signing-fields', snapshotId],
    queryFn: () => dsCrossOrgSnapshotService.getSigningFields(snapshotId),
  });

  // Placeholders
  const { data: placeholders = [] } = useQuery({
    queryKey: ['dsCrossOrgSnapshot-placeholders', snapshotId],
    queryFn: () => dsCrossOrgSnapshotService.getDataPlaceholders(snapshotId),
  });

  // Review + activities — cross-org getReview doesn't nest activities, so fetch separately
  // and merge them into the review prop passed to ChatWidget (which reads r.activities).
  // refetchInterval: 10_000 matches TemplateDetail's pattern — safety net for SSE disconnects
  // + catches events that fire between SSE reconnects (no Last-Event-ID replay on backend).
  const { data: review } = useQuery({
    queryKey: ['dsCrossOrgSnapshot-review', snapshotId],
    queryFn: () => dsCrossOrgSnapshotService.getReview(snapshotId),
    refetchInterval: 10_000,
  });
  const { data: reviewActivities } = useQuery({
    queryKey: ['dsCrossOrgSnapshot-review-activities', snapshotId],
    queryFn: () => dsCrossOrgSnapshotService.getReviewActivities(snapshotId),
    refetchInterval: 10_000,
  });
  const reviewWithActivities = useMemo(() => {
    if (!review) return null;
    return { ...review, activities: reviewActivities ?? [] };
  }, [review, reviewActivities]);

  // Live updates — the generic review stream broadcasts events from all sources:
  // reviewer-submit, reviewer-complete, reviewer-note (tenant or external), plus any
  // workpay admin actions on the same review. useReviewSse (author mode) invalidates
  // ['ds-reviews', targetType, targetId] on events + on reconnect. The bridge below
  // catches those invalidations and re-invalidates our cross-org keys.
  useReviewSse({
    mode: 'author',
    targetType: 'TEMPLATE',
    targetId: snapshotId,
  });

  // Bridge ChatWidget invalidation + useReviewSse invalidation → both fire
  // ['ds-reviews', ...] which doesn't match our cross-org keys. Catch and re-invalidate.
  useEffect(() => {
    const unsub = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        Array.isArray(event.query.queryKey) &&
        event.query.queryKey[0] === 'ds-reviews' &&
        event.query.queryKey[2] === snapshotId
      ) {
        void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot-review', snapshotId] });
        void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot-review-activities', snapshotId] });
      }
    });
    return unsub;
  }, [queryClient, snapshotId]);

  // PDF blob (for PDF format view mode)
  const isPdf = snapshot?.templateFormat === 'PDF';
  const isDocx = snapshot?.templateFormat === 'DOCX';
  const pdfBlobQuery = useQuery({
    queryKey: ['crossOrgSnapshotPdfBlob', snapshotId, snapshot?.sourceFileId],
    queryFn: async () => {
      const res = isPdf
        ? await dsFileService.download(snapshot!.sourceFileId)
        : await dsCrossOrgSnapshotService.preview(snapshotId);
      return URL.createObjectURL(res.data);
    },
    enabled: !!snapshot?.sourceFileId,
    staleTime: Infinity,
  });
  const pdfUrl = pdfBlobQuery.data;
  const pdfLoading = pdfBlobQuery.isLoading;

  // Revoke the PREVIOUS blob URL when a new one arrives. Do NOT revoke on unmount —
  // the query cache (staleTime: Infinity) holds this URL and serves it on next mount,
  // so unmount-revoke would cause "Failed to load PDF" on re-entry.
  const prevPdfUrlRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevPdfUrlRef.current && prevPdfUrlRef.current !== pdfUrl) {
      URL.revokeObjectURL(prevPdfUrlRef.current);
    }
    prevPdfUrlRef.current = pdfUrl;
  }, [pdfUrl]);

  // Editor config for OnlyOffice. Mode choice matters for docKey alignment:
  // - IN_REVIEW  → `review-author` → stable `review_<reviewHex>_<fileHex>` docKey
  //   (SAME key as tenant reviewer + external reviewer → all co-edit in ONE
  //    OnlyOffice session; admin sees tenant's tracked changes live and has
  //    accept/reject powers).
  // - DRAFT/other → `edit` → `xorg_<workpayOrgHex>_<fileHex>` docKey (workpay-
  //   only edit session, no reviewer overlap).
  // See contract-service docs: /cross-org/snapshots/{id}/editor-config.
  const canEditInOO = hasPermission('CONTRACT_TEMPLATES_UPDATE') && snapshot?.status === 'IN_REVIEW' && isDocx;
  const editorMode = snapshot?.status === 'IN_REVIEW' ? 'review-author' : 'edit';
  const { data: editorConfig } = useQuery({
    queryKey: ['dsCrossOrgSnapshotEditorConfig', snapshotId, editorMode],
    queryFn: () => dsCrossOrgSnapshotService.getEditorConfig(snapshotId, editorMode),
    enabled: !!canEditInOO,
    staleTime: Infinity,
  });

  // ── Mutations ──
  const approveMutation = useMutation({
    mutationFn: () => dsCrossOrgSnapshotService.approve(snapshotId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot', snapshotId] });
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshots'] });
      addNotification({ variant: 'success', title: t('K_SNAPSHOT_ADMIN_APPROVED', 'Contract approved successfully') });
      setShowApproveModal(false);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (reason: string) => dsCrossOrgSnapshotService.deactivate(snapshotId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot', snapshotId] });
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshots'] });
      addNotification({ variant: 'success', title: t('K_SNAPSHOT_ADMIN_DEACTIVATED', 'Contract deactivated') });
      setShowDeactivateModal(false);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  // OnlyOffice keeps edits in an in-memory document session and only persists
  // them to the backing file via its forcesave callback. Without this hop the
  // scan reads the stale persisted DOCX and misses recent edits — see the
  // canonical pattern in DataPlaceholderPanel.scanMutation.
  const docKey =
    typeof (editorConfig as { document?: { key?: unknown } } | undefined)?.document?.key === 'string'
      ? ((editorConfig as { document: { key: string } }).document.key)
      : undefined;

  const scanPlaceholdersMutation = useMutation({
    mutationFn: async () => {
      if (docKey) {
        try {
          await dsCrossOrgSnapshotService.forceSave(docKey);
        } catch {
          // Continue even if forcesave fails — the scan still surfaces whatever
          // is on disk; better than silently skipping a user-initiated action.
        }
        // Wait for OnlyOffice's callback to land the saved bytes on the backing
        // file before scanning. Matches the 3s wait used in DataPlaceholderPanel.
        await new Promise((r) => setTimeout(r, 3000));
      }
      return dsCrossOrgSnapshotService.scanPlaceholders(snapshotId);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot-placeholders', snapshotId] });
      addNotification({
        variant: 'success',
        title: t('K_SNAPSHOT_ADMIN_SCAN_DONE', 'Found {count} placeholders').replace('{count}', String(result.totalFound)),
      });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const markAuthorCompleteMutation = useMutation({
    mutationFn: () => dsCrossOrgSnapshotService.markAuthorComplete(snapshotId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot', snapshotId] });
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot-review', snapshotId] });
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot-review-activities', snapshotId] });
      addNotification({ variant: 'success', title: t('K_REVIEW_AUTHOR_COMPLETE_SUCCESS', 'Marked as complete.') });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  // ── Handlers ──

  // Direct invalidation for admin's own sent notes — the ChatWidget's hardcoded
  // ['ds-reviews', ...] invalidation is a no-op because that key never exists in
  // SnapshotAdminDetail's cache. Without this callback, admin's sent messages only
  // appear after the 10s poll. With it, they appear immediately.
  const handleNoteSent = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot-review', snapshotId] });
    void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot-review-activities', snapshotId] });
  }, [queryClient, snapshotId]);

  const isInReview = snapshot?.status === 'IN_REVIEW';
  const isReviewCompleted = snapshot?.status === 'REVIEW_COMPLETED';
  const isActive = snapshot?.status === 'ACTIVE';
  // Display label for snapshot.status. IN_REVIEW / REVIEW_COMPLETED need explicit
  // copy; everything else falls through to toTitleCase ("ACTIVE" → "Active").
  const statusLabel = isInReview
    ? t('K_SNAPSHOT_STATUS_IN_REVIEW', 'In Review')
    : isReviewCompleted
      ? t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete')
      : snapshot
        ? toTitleCase(snapshot.status)
        : '';

  // --- Forbidden (page-level — detail route can't render anything without the snapshot) ---
  if (snapshotForbidden) {
    return (
      <div className={styles.page}>
        <Forbidden
          variant="page"
          onAction={() => navigate({ to: '/w/contracts' })}
          actionLabel={t('K_BUTTON_GO_BACK', 'Go Back')}
        />
      </div>
    );
  }

  if (isLoading || !snapshot) {
    return <div className={styles.loading}>{t('K_SNAPSHOT_LOADING', 'Loading contract...')}</div>;
  }

  // ── View mode ──
  const expandLabel = isExpanded ? t('K_DS_EXIT_FULLSCREEN', 'Exit fullscreen') : t('K_DS_FULLSCREEN', 'Fullscreen');

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={() => navigate({ to: '/w/contracts' })}>
            {'\u2190'} {t('K_BREADCRUMB_CONTRACTS', 'Contracts')}
          </button>
          <h1 className={styles.title} title={snapshot.name}>
            {snapshot.name}
          </h1>
          <span className={`${styles.badge} ${styles[STATUS_CLASS_MAP[snapshot.status] ?? 'badgeNeutral'] ?? ''}`}>
            {statusLabel}
          </span>
          <span className={`${styles.badge} ${styles[FORMAT_CLASS_MAP[snapshot.templateFormat] ?? 'badgeNeutral'] ?? ''}`}>
            {snapshot.templateFormat}
          </span>
        </div>
        <div className={styles.headerActions}>
          <ProfileActionsMenu
            actions={(() => {
              const items: ProfileAction[] = [];
              // Backend approve() accepts both IN_REVIEW and REVIEW_COMPLETED.
              if ((isInReview || isReviewCompleted) && hasPermission('CONTRACT_TEMPLATES_APPROVE')) {
                items.push({
                  id: 'approve',
                  label: t('K_DS_ACTIVATE', 'Approve'),
                  variant: 'primary',
                  onClick: () => setShowApproveModal(true),
                });
              }
              if (isInReview && review && !review.authorCompleted && hasPermission('CONTRACT_TEMPLATES_UPDATE')) {
                items.push({
                  id: 'mark-review-complete',
                  label: t('K_BUTTON_MARK_COMPLETE', 'Mark as Complete'),
                  variant: 'primary',
                  onClick: () => markAuthorCompleteMutation.mutate(),
                });
              }
              if (isInReview && isDocx && hasPermission('CONTRACT_TEMPLATES_UPDATE')) {
                items.push({
                  id: 'scan-placeholders',
                  label: t('K_SNAPSHOT_ADMIN_SCAN_PLACEHOLDERS', 'Scan Placeholders'),
                  onClick: () => scanPlaceholdersMutation.mutate(),
                });
              }
              if (isActive && hasPermission('CONTRACT_TEMPLATES_DEACTIVATE')) {
                items.push({
                  id: 'deactivate',
                  label: t('K_DS_DEACTIVATE', 'Deactivate'),
                  onClick: () => setShowDeactivateModal(true),
                });
              }
              return items;
            })()}
          />
        </div>
      </div>

      <div className={styles.splitContainer} ref={splitContainerRef}>
        {/* Left panel */}
        <div className={styles.leftPanel} style={{ width: `${splitPercent}%` }}>
          <div className={styles.leftPanelScroll}>
            {/* Details */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('K_SNAPSHOT_DETAIL_DETAILS', 'Details')}</h2>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_LABEL_NAME', 'Name')}</span>
                  <span className={styles.detailValue}>{snapshot.name}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_COLUMN_TENANT', 'Client')}</span>
                  <span className={styles.detailValue}>{tenantName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_LABEL_DESCRIPTION', 'Description')}</span>
                  <span className={styles.detailValue}>{snapshot.description || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_COUNTRY', 'Country')}</span>
                  <span className={styles.detailValue}>{countryName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_LEGAL_ENTITY', 'Legal Entity')}</span>
                  <span className={styles.detailValue}>{legalEntityName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}</span>
                  <span className={styles.detailValue}>{employmentTypeName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_DOCUMENT_TYPE', 'Document Type')}</span>
                  <span className={styles.detailValue}>{documentTypeName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_LABEL_FORMAT', 'Format')}</span>
                  <span className={styles.detailValue}>{snapshot.templateFormat}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_COLUMN_STATUS', 'Status')}</span>
                  <span className={styles.detailValue}>
                    {statusLabel}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_COLUMN_PAGES', 'Pages')}</span>
                  <span className={styles.detailValue}>{snapshot.pageCount || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Review panel — full review management (reviewers, activities, actions) */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('K_REVIEW', 'Review')}</h2>
              <SnapshotReviewPanel snapshotId={snapshotId} />
            </div>

            {/* Signing fields */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                  {t('K_DS_SIGNING_FIELDS', 'Signing Fields')} ({signingFields.length})
                </h2>
              </div>
              {signingFields.length === 0 ? (
                <p className={styles.emptyText}>{t('K_DS_NO_SIGNING_FIELDS', 'No signing fields configured.')}</p>
              ) : (
                <div className={styles.fieldList}>
                  {signingFields.map((f) => (
                    <div key={f.id} className={styles.fieldCard}>
                      <div className={styles.fieldCardTop}>
                        <span className={styles.fieldType}>{f.fieldType}</span>
                        <span className={styles.fieldLabel}>{f.label || '\u2014'}</span>
                      </div>
                      <span className={styles.fieldMeta}>
                        {t('K_DS_PAGE', 'Page')} {f.pageNumber} · {t('K_DS_SIGNER', 'Signer')} {f.recipientIndex}
                        {f.isRequired ? ` · ${t('K_DS_REQUIRED', 'Required')}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Placeholders (DOCX only) */}
            {isDocx && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>
                  {t('K_DS_DATA_PLACEHOLDERS', 'Data Placeholders')} ({placeholders.length})
                </h2>
                {placeholders.length === 0 ? (
                  <p className={styles.emptyText}>{t('K_DS_NO_DATA_PLACEHOLDERS', 'No data placeholders configured.')}</p>
                ) : (
                  <div className={styles.fieldList}>
                    {placeholders.map((p) => (
                      <div key={p.placeholderKey} className={styles.fieldCard}>
                        <div className={styles.fieldCardTop}>
                          <span className={styles.fieldType}>{p.fieldType || 'TEXT'}</span>
                          <span className={styles.fieldLabel} style={{ fontFamily: 'monospace' }}>
                            {`{{${p.placeholderKey}}}`}
                          </span>
                        </div>
                        <span className={styles.fieldMeta}>
                          {p.dataSourceType}{p.dataSourceField ? ` → ${p.dataSourceField}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Chat widget — workpay admin uses authenticated /v1/reviews/{id}/note via dsReviewService.
              We pass reviewWithActivities because cross-org getReview doesn't nest activities. */}
          {reviewWithActivities && (
            <ChatWidget
              targetType="TEMPLATE"
              targetId={snapshotId}
              reviews={[reviewWithActivities]}
              mode="author"
              onNoteSent={handleNoteSent}
              positioning="absolute"
            />
          )}
        </div>

        {/* Draggable split handle */}
        <div className={styles.splitHandle} onMouseDown={handleSplitStart} />

        {/* Right panel */}
        <div className={`${styles.rightPanel} ${isExpanded ? styles.rightPanelExpanded : ''}`}>
          <div className={styles.previewToolbar}>
            <span className={styles.previewToolbarTitle}>{snapshot.name}</span>
            <span className={styles.previewToolbarMeta}>
              {snapshot.pageCount} {snapshot.pageCount === 1 ? t('K_DS_PAGE', 'page') : t('K_DS_PAGES', 'pages')}
            </span>
            <button
              className={styles.expandBtn}
              onClick={() => setIsExpanded((v) => !v)}
              aria-label={expandLabel}
              title={expandLabel}
            >
              {isExpanded ? '\u2296' : '\u2295'}
            </button>
          </div>
          <div className={styles.previewContent}>
            {canEditInOO && editorConfig ? (
              <OnlyOfficeEditor
                fileId={snapshot.sourceFileId}
                mode="edit"
                configOverride={editorConfig}
              />
            ) : pdfLoading ? (
              <div className={styles.pdfLoader}>
                <div className={styles.spinner} />
                {t('K_DS_LOADING_PREVIEW', 'Loading preview...')}
              </div>
            ) : pdfUrl ? (
              <div className={styles.pdfViewerWrap}>
                <PdfViewer
                  url={pdfUrl}
                  pageNumber={1}
                  fields={signingFields}
                  allFields={signingFields}
                  selectedFieldIndex={null}
                  onFieldSelect={() => {}}
                  onFieldMove={() => {}}
                  zoom={1}
                  onDocumentLoad={() => {}}
                  onVisiblePageChange={() => {}}
                />
              </div>
            ) : (
              <div className={styles.pdfLoader}>
                {t('K_DS_LOADING_PREVIEW', 'Loading preview...')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Approve modal */}
      <AlertModal
        variant="warning"
        title={t('K_SNAPSHOT_ADMIN_APPROVE_TITLE', 'Approve Contract')}
        description={t('K_SNAPSHOT_ADMIN_APPROVE_DESC', 'Are you sure you want to approve "{name}"? This will make the contract active for the client.').replace('{name}', snapshot.name)}
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        primaryLabel={t('K_DS_ACTIVATE', 'Approve')}
        onPrimaryClick={() => approveMutation.mutate()}
        loading={approveMutation.isPending}
      />

      {/* Deactivate modal */}
      <DeactivateModal
        isOpen={showDeactivateModal}
        snapshotName={snapshot.name}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={(reason) => deactivateMutation.mutate(reason)}
        loading={deactivateMutation.isPending}
      />
    </div>
  );
}
